import {
	Show,
	Switch,
	type SwitchControlProps,
	type SwitchLabelProps,
	type SwitchRootProps,
	type SwitchThumbProps,
} from '@chakra-ui/react';
import type React from 'react';

interface ISwitchControlProps extends SwitchControlProps {
	children?: React.ReactNode;
	bg?: string | { _dark: string; _light: string };
}

interface ISwitchLabelProps extends SwitchLabelProps {
	children?: React.ReactNode;
}

interface ISwitchThumbProps extends SwitchThumbProps {
	children?: React.ReactNode;
}

const CustomSwitch = {
	Root: (props: React.ComponentProps<typeof Switch.Root>) => (
		<Switch.Root {...props} />
	),
	HiddenInput: (props: React.ComponentProps<typeof Switch.HiddenInput>) => (
		<Switch.HiddenInput {...props} />
	),
	Control: (props: ISwitchControlProps) => <Switch.Control {...props} />,
	Thumb: (props: ISwitchThumbProps) => <Switch.Thumb {...props} />,
	Label: (props: ISwitchLabelProps) => <Switch.Label {...props} />,
	ThumbIndicator: (
		props: React.ComponentProps<typeof Switch.ThumbIndicator>,
	) => <Switch.ThumbIndicator {...props} />,
};

export interface FaceliftSwitchProps extends SwitchRootProps {
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	label?: string;
	labelPosition?: 'left' | 'right';
	checkedThumbIcon?: React.ReactNode;
	unCheckedThumbIcon?: React.ReactNode;
}

const FaceliftSwitch: React.FC<FaceliftSwitchProps> = ({
	checked,
	onCheckedChange,
	label,
	labelPosition = 'right',
	checkedThumbIcon,
	unCheckedThumbIcon,
	...rest
}) => {
	const controlComponent = (
		<Show
			when={checkedThumbIcon || unCheckedThumbIcon}
			fallback={
				<CustomSwitch.Control
					bg={{
						_dark: checked ? 'brand.solidGradient' : 'jetBlack',
						_light: checked ? 'brand.success' : '',
					}}
				>
					<CustomSwitch.Thumb />
				</CustomSwitch.Control>
			}
		>
			<CustomSwitch.Control
				bg={{
					_dark: checked ? 'brand.success' : 'jetBlack',
					_light: checked ? 'brand.success' : '',
				}}
			>
				<CustomSwitch.Thumb>
					<CustomSwitch.ThumbIndicator fallback={unCheckedThumbIcon}>
						{checkedThumbIcon}
					</CustomSwitch.ThumbIndicator>
				</CustomSwitch.Thumb>
			</CustomSwitch.Control>
		</Show>
	);

	const labelComponent = (
		<Show when={!!label}>
			<CustomSwitch.Label>{label}</CustomSwitch.Label>
		</Show>
	);

	return (
		<CustomSwitch.Root
			checked={checked}
			onCheckedChange={(e: { checked: boolean }) =>
				onCheckedChange(!!e.checked)
			}
			{...rest}
		>
			<CustomSwitch.HiddenInput />

			<Show
				when={labelPosition === 'left'}
				fallback={
					<>
						{controlComponent}
						{labelComponent}
					</>
				}
			>
				{labelComponent}
				{controlComponent}
			</Show>
		</CustomSwitch.Root>
	);
};

export default FaceliftSwitch;
